import type { FormikErrors } from 'formik'
import { Formik, validateYupSchema, yupToFormErrors } from 'formik'
import cloneDeep from 'lodash-es/cloneDeep'
import isEqual from 'lodash-es/isEqual'
import map from 'lodash-es/map'
import set from 'lodash-es/set'
import unset from 'lodash-es/unset'
import { reverse } from 'named-urls'
import React from 'react'
import { useLocation } from 'react-router-dom'

import { Dashboard, PageLayout } from '@components/common'
import { LeavingGuard } from '@components/utils/LeavingGuard'
import {
  BackButton,
  HeadContent,
  Loading,
  SaveShortcutListener,
  Toasts,
} from '@fourthwall/components'
import type { VariantType } from '@fourthwall/services/dist/models/variantType'
import { formatCurrency } from '@fourthwall/utils/dist/currency'
import { useCurrentShop } from '@modules/Dashboard/hooks/useCurrentShop'
import { ConfirmBar } from '@modules/Dashboard/modules/ConfirmBar'
import { routing } from '@utils/routing'

import { AddButtons } from './components/AddButtons'
import { ProductVariantType } from './components/ProductVariantType'
import { validationSchema } from './consts'
import type { FormValues, ProductVariantsProps } from './types'

// import type { UpdateVariantTypesRequestPayload } from '../store/product/types'

export const ProductVariantsView = ({
  name = '',
  productId,
  productVariantTypes,
  productIsFetching,
  updateVariantTypesRequest,
}: ProductVariantsProps) => {
  const location = useLocation()
  const { currentShop } = useCurrentShop()

  const validate = (values: FormValues) => {
    // NOTE: https://github.com/jaredpalmer/formik/issues/506
    try {
      validateYupSchema<FormValues>(values, validationSchema, true, { values })
    } catch (err) {
      return yupToFormErrors(err)
    }

    return {}
  }

  const getInitialValues = (): FormValues => ({
    types: cloneDeep(productVariantTypes).map((variantType) =>
      set(
        variantType,
        'options',
        variantType.options.map((variant) =>
          Object.assign(variant, {
            price: variant.price?.value
              ? { value: formatCurrency(variant.price.value), currency: variant.price.currency }
              : {},
            weight: variant.weight || {},
          }),
        ),
      ),
    ),
  })

  const onlyVaryingBy = (
    variantType: VariantType,
    variantTypes: VariantType[],
    fieldName: string,
  ) => {
    if (!(variantType.variesBy as any)[fieldName]) return false

    return !variantTypes.some((vt) => vt !== variantType && (vt.variesBy as any)[fieldName])
  }

  const getPayload = (values: FormValues): FormValues => {
    const payload = cloneDeep(values)

    payload.types.forEach((variantType) => {
      if (!onlyVaryingBy(variantType, payload.types, 'weight')) {
        variantType.options.forEach((variant) => unset(variant, 'weight'))
      }

      variantType.options.forEach((option) => {
        set(option, 'name', option.name.trim())
        set(option, 'price', null)
      })

      set(variantType, 'title', variantType.title.trim())
    })

    return payload
  }

  const handleFormConfirm = (submitForm: any, isValid: any) => {
    submitForm()

    if (!isValid) {
      Toasts.notify('There are errors that need to be fixed before saving.', { type: 'error' })
    }
  }

  const handleFormSubmit = (values: FormValues) => {
    // @ts-ignore
    updateVariantTypesRequest(getPayload(values))
  }

  const initialValues = getInitialValues()

  const renderVariantType = (
    variantType: VariantType,
    index: number,
    allValues: VariantType[],
    errors: FormikErrors<FormValues>,
    setValues: (values: FormValues) => void,
  ) => (
    <ProductVariantType
      key={variantType.type}
      index={index}
      variantType={variantType}
      shopWeightUnit={currentShop?.settings.weightUnit}
      allValues={allValues}
      errors={errors}
      setValues={setValues}
    />
  )

  return (
    <Loading isLoading={productIsFetching}>
      <HeadContent title={`Variants \u2013 ${name}`} />
      <PageLayout size="large">
        <Dashboard.Section
          name="Edit, add, or remove variants"
          topContent={
            <BackButton
              label={name}
              to={{
                pathname: reverse(routing.products.all.product.self, { productId }),
                search: location.search,
              }}
            />
          }
        >
          <Formik
            initialValues={initialValues}
            onSubmit={handleFormSubmit}
            validate={validate}
            enableReinitialize
          >
            {({ values, errors, submitForm, setValues, resetForm, isValid }) => {
              const isFormDirty = !isEqual(initialValues, values)

              return (
                <>
                  <div>
                    {map(values.types, (variantType, index) => {
                      return renderVariantType(variantType, index, values.types, errors, setValues)
                    })}
                  </div>
                  <AddButtons allValues={values.types} setValues={setValues} />
                  <SaveShortcutListener disabled={!isFormDirty} onSave={submitForm} />
                  <ConfirmBar
                    isOpen={isFormDirty}
                    isLoading={productIsFetching}
                    onConfirm={() => handleFormConfirm(submitForm, isValid)}
                    onCancel={() => resetForm({ values: initialValues })}
                  />
                  <LeavingGuard when={isFormDirty} isValid={isValid} onSave={submitForm} />
                </>
              )
            }}
          </Formik>
        </Dashboard.Section>
      </PageLayout>
    </Loading>
  )
}
